import { useEffect, useRef, useState } from "react";
import { Bot, CheckCircle2, Download, LoaderCircle, Mail, MessageCircle, Send, Trash2, X } from "lucide-react";
import API, { extractApiError } from "../../services/api";
import { baseUrl } from "../../services/BaseURL";

const STORAGE_KEY = "prosale:ollama-chat";
const WELCOME = {
  role: "assistant",
  content: "Hi, I am LiMo. How can I help?",
};

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20)));
    } catch {
      // Session storage is optional.
    }
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, sending]);

  useEffect(() => {
    if (!open || status) return;
    API.get("/ai/status", { timeout: 10_000 })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ enabled: true, configured: false }));
  }, [open, status]);

  async function sendMessage(event) {
    event?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const userMessage = { role: "user", content: text };
    const history = messages
      .slice(1)
      .filter((message) => !message.error)
      .slice(-12)
      .map(({ role, content }) => ({ role, content }));

    setMessages((current) => [...current, userMessage]);
    setInput("");
    setSending(true);

    try {
      const { data } = await API.post(
        "/ai/chat",
        { message: text, history },
        { timeout: 130_000 }
      );
      setMessages((current) => [
        ...current,
        // `download` is present only when the user asked for a file. The
        // backend has already resolved which report, which format and which
        // date range — this just renders the button.
        {
          role: "assistant",
          content: data.answer,
          download: data.download || null,
          // A drafted email waiting for the user's Send click. Nothing has been sent.
          email: data.pendingEmail ? { ...data.pendingEmail, state: "draft" } : null,
        },
      ]);
      setStatus((current) => ({
        ...(current || {}),
        enabled: true,
        configured: true,
        model: data.model,
      }));
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          error: true,
          content: extractApiError(error, "The AI assistant is unavailable."),
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  function clearChat() {
    setMessages([WELCOME]);
    setInput("");
  }

  return (
    <>
      {open && (
        <section
          aria-label="AI assistant"
          className="fixed bottom-20 right-4 z-50 flex h-[min(560px,calc(100vh-7rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        >
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4 dark:border-slate-700">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <Bot size={18} />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  LiMo
                </h2>
                <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {status?.enabled && status?.configured
                    ? "Private AI assistant"
                    : status
                    ? status.enabled
                      ? "Ollama configuration required"
                      : "AI module disabled"
                    : "Checking connection..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={clearChat}
                title="Clear conversation"
                className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <Trash2 size={16} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close assistant"
                className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={[
                    "max-w-[86%] whitespace-pre-wrap break-words px-3 py-2 text-sm leading-5",
                    message.role === "user"
                      ? "rounded-lg rounded-br-sm bg-blue-600 text-white"
                      : message.error
                      ? "rounded-lg rounded-bl-sm bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
                      : "rounded-lg rounded-bl-sm bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-100",
                  ].join(" ")}
                >
                  <ChatMessageContent content={message.content} />
                  {message.download && <DownloadButton file={message.download} />}
                  {message.email && (
                    <EmailDraftCard
                      email={message.email}
                      onChange={(email) =>
                        setMessages((current) => current.map((m, i) => (i === index ? { ...m, email } : m)))
                      }
                    />
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-500 dark:bg-slate-800 dark:text-gray-300">
                  <LoaderCircle size={15} className="animate-spin" />
                  Thinking...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={sendMessage}
            className="flex shrink-0 items-end gap-2 border-t border-gray-200 p-3 dark:border-slate-700"
          >
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              rows={1}
              maxLength={4000}
              placeholder="Ask LiMo..."
              className="max-h-28 min-h-10 flex-1 resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              title="Send message"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? (
                <LoaderCircle size={17} className="animate-spin" />
              ) : (
                <Send size={17} />
              )}
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        title={open ? "Close AI assistant" : "Open AI assistant"}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-50 grid h-12 w-12 place-items-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/25"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
}

/**
 * Download button attached to an assistant reply.
 *
 * Fetches through the API client rather than rendering a plain <a href>,
 * because the export endpoint requires the Authorization header and a browser
 * navigation does not send it — the link would 401 and look broken. Fetching
 * as a blob and clicking a synthetic anchor keeps the auth and gives the file
 * a proper filename.
 */
/**
 * An email the assistant prepared. The server sends it ONLY when this Send
 * button is clicked (POST /ai/email/{id}/send) — the AI itself cannot send.
 * Subject and body can be edited here first; recipients cannot.
 */
function EmailDraftCard({ email, onChange }) {
  const [subject, setSubject] = useState(email.subject || "");
  const [body, setBody] = useState(email.body || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const names = (list) => (list || []).map((r) => `${r.name} <${r.email}>`).join(", ");
  const expired = email.expiresAt && Date.parse(email.expiresAt) < Date.now();

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      await API.post(`/ai/email/${email.id}/send`, { subject, body }, { timeout: 60_000 });
      onChange({ ...email, subject, body, state: "sent" });
    } catch (e) {
      setError(extractApiError(e, "The email could not be sent."));
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => {
    API.post(`/ai/email/${email.id}/cancel`).catch(() => {});
    onChange({ ...email, state: "cancelled" });
  };

  if (email.state === "sent" || email.state === "cancelled") {
    return (
      <div className="mt-2 flex items-center gap-1.5 whitespace-normal rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900">
        {email.state === "sent"
          ? <><CheckCircle2 size={14} className="text-emerald-600" /> Email sent to {names(email.to)}</>
          : <><X size={14} className="text-gray-500" /> Email discarded — nothing was sent.</>}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2 whitespace-normal rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-800 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-100">
      <div className="flex items-center gap-1.5 font-semibold">
        <Mail size={14} className="text-blue-600" /> Email draft — not sent yet
      </div>
      <div><span className="text-gray-500 dark:text-gray-400">To: </span>{names(email.to)}</div>
      {email.cc?.length > 0 && <div><span className="text-gray-500 dark:text-gray-400">Cc: </span>{names(email.cc)}</div>}
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={200}
        aria-label="Subject"
        className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-950"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={Math.min(12, Math.max(4, body.split("\n").length + 1))}
        aria-label="Message"
        className="w-full rounded border border-gray-200 bg-white px-2 py-1 font-mono text-[11px] leading-4 dark:border-slate-600 dark:bg-slate-950"
      />
      {error && <div role="alert" className="text-rose-600 dark:text-rose-300">{error}</div>}
      {expired ? (
        <div className="text-amber-600 dark:text-amber-300">This draft has expired. Ask LiMo to prepare it again.</div>
      ) : (
        <div className="flex justify-end gap-2">
          <button type="button" onClick={cancel} disabled={busy}
            className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button type="button" onClick={send} disabled={busy || !subject.trim() || !body.trim()}
            className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? <LoaderCircle size={13} className="animate-spin" /> : <Send size={13} />} Send
          </button>
        </div>
      )}
    </div>
  );
}

function DownloadButton({ file }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");

  const download = async () => {
    setBusy(true);
    setFailed("");
    try {
      const res = await API.get(file.url, { responseType: "blob", timeout: 120_000 });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = file.fileName || "report";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Release the object URL, or the blob is pinned in memory until reload.
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setFailed(
        error?.response?.status === 403
          ? "You don't have permission to export reports."
          : "Couldn't build the file. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-200 dark:hover:bg-slate-800"
      >
        {busy ? <LoaderCircle size={14} className="animate-spin" /> : <Download size={14} />}
        {busy ? "Preparing…" : `Download ${String(file.format || "").toUpperCase()}`}
      </button>
      <p className="mt-1 text-[11px] text-gray-500">{file.fileName}</p>
      {failed && <p className="mt-1 text-[11px] text-rose-600">{failed}</p>}
    </div>
  );
}

/**
 * Renders an HTML block the assistant produced, inside a sandboxed iframe.
 *
 * ── Why an iframe with sandbox="" and not dangerouslySetInnerHTML ──────────
 * The HTML is model output, and the model builds it from LiMo records —
 * employee names, leave reasons, client names, check-in addresses. All of that
 * is text someone typed into the app. A leave reason containing a <script> tag
 * would, with innerHTML, execute inside the admin session: same origin, same
 * cookies, same token in localStorage.
 *
 * `sandbox=""` — deliberately empty, granting NOTHING — blocks scripts, forms,
 * popups, top-level navigation and same-origin access. CSS and layout still
 * work, which is all a report table needs. srcdoc keeps the content inline so
 * nothing is fetched over the network.
 *
 * This is not paranoia about the model. It is that the model is faithfully
 * echoing data it was given, and that data is not trusted input.
 */
function HtmlPreview({ html }) {
  const [showCode, setShowCode] = useState(false);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1 dark:border-slate-700 dark:bg-slate-800">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Preview
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-auto rounded px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-700"
        >
          {expanded ? "Shrink" : "Expand"}
        </button>
        <button
          type="button"
          onClick={() => setShowCode((v) => !v)}
          className="rounded px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-700"
        >
          {showCode ? "Preview" : "Code"}
        </button>
      </div>

      {showCode ? (
        <pre className="max-h-64 overflow-auto bg-gray-900 p-2 text-[11px] leading-4 text-gray-100">
          {html}
        </pre>
      ) : (
        <iframe
          title="Assistant preview"
          srcDoc={html}
          sandbox=""
          className="w-full border-0 bg-white"
          style={{ height: expanded ? 520 : 240 }}
        />
      )}
    </div>
  );
}

/**
 * Split a reply into fenced code blocks and prose.
 *
 * Returns segments of {type: "html" | "code" | "text", value, lang}. Done with
 * an explicit scan rather than split() on a capturing regex, because the
 * fence's language tag has to survive into the segment to decide whether it is
 * previewable.
 */
function splitFences(text) {
  const out = [];
  const fence = /```([a-zA-Z0-9]*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = fence.exec(text)) !== null) {
    if (m.index > last) out.push({ type: "text", value: text.slice(last, m.index) });
    const lang = (m[1] || "").toLowerCase();
    const body = m[2] || "";
    // Treat it as previewable HTML only when it declares itself as such AND
    // actually contains markup — a ```html block holding a sentence would
    // render as an empty white box, which looks broken.
    const isHtml = (lang === "html" || lang === "htm") && /<\/?[a-z][\s\S]*>/i.test(body);
    out.push({ type: isHtml ? "html" : "code", value: body, lang });
    last = fence.lastIndex;
  }
  // An UNCLOSED fence — the model ran out of tokens mid-block, which happens on
  // long tables. Preview what arrived rather than dumping the raw opener.
  if (last < text.length) {
    const rest = text.slice(last);
    const open = rest.match(/```([a-zA-Z0-9]*)\n?([\s\S]*)$/);
    if (open && /<\/?[a-z][\s\S]*>/i.test(open[2] || "")) {
      out.push({ type: "html", value: open[2], lang: (open[1] || "").toLowerCase() });
    } else {
      out.push({ type: "text", value: rest });
    }
  }
  return out;
}

function ChatMessageContent({ content }) {
  const text = String(content || "");
  const segments = splitFences(text);

  // Fast path: no fences at all, which is the common case.
  if (segments.length > 1 || segments.some((seg) => seg.type !== "text")) {
    return (
      <div className="space-y-1">
        {segments.map((seg, i) => {
          if (seg.type === "html") return <HtmlPreview key={i} html={seg.value} />;
          if (seg.type === "code") {
            return (
              <pre
                key={i}
                className="my-2 max-h-64 overflow-auto rounded-lg bg-gray-900 p-2 text-[11px] leading-4 text-gray-100"
              >
                {seg.value}
              </pre>
            );
          }
          return <MarkdownText key={i} text={seg.value} />;
        })}
      </div>
    );
  }

  return <MarkdownText text={text} />;
}

/* ── Minimal Markdown ─────────────────────────────────────────────────────
 * The assistant answers in Markdown (tables, **bold**). It was shown as raw
 * text — pipes and asterisks. This renders the parts it actually uses:
 * tables, bold, italics and headings. No HTML is ever injected: everything
 * becomes React elements, so model output cannot run script here. */

const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line);
const isDividerLine = (line) => /^\s*\|?(\s*:?-{2,}:?\s*\|)+\s*:?-{0,}:?\s*\|?\s*$/.test(line);
const tableCells = (line) => line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

function MarkdownText({ text }) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let buffer = [];
  const flush = () => {
    if (buffer.length) blocks.push({ type: "text", value: buffer.join("\n") });
    buffer = [];
  };
  for (let i = 0; i < lines.length; i++) {
    // A table: a header row followed by a |---|---| divider row.
    if (isTableLine(lines[i]) && i + 1 < lines.length && isDividerLine(lines[i + 1])) {
      flush();
      const header = tableCells(lines[i]);
      const rows = [];
      i += 2;
      while (i < lines.length && isTableLine(lines[i])) {
        rows.push(tableCells(lines[i]));
        i++;
      }
      i--;
      blocks.push({ type: "table", header, rows });
      continue;
    }
    buffer.push(lines[i]);
  }
  flush();

  return (
    <div className="space-y-2">
      {blocks.map((b, i) =>
        b.type === "table" ? (
          <div key={i} className="max-w-full overflow-x-auto">
            <table className="min-w-full border-collapse whitespace-normal text-xs">
              <thead>
                <tr>
                  {b.header.map((h, j) => (
                    <th key={j} className="border-b border-gray-300 px-2 py-1 text-left font-semibold dark:border-slate-600">
                      <InlineMarkdown text={h} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, j) => (
                  <tr key={j} className="odd:bg-white/60 dark:odd:bg-slate-900/40">
                    {b.header.map((_, k) => (
                      <td key={k} className="border-b border-gray-200 px-2 py-1 align-top dark:border-slate-700">
                        <PlainText text={r[k] ?? ""} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <PlainText key={i} text={b.value} />
        )
      )}
    </div>
  );
}

/** **bold**, _italic_ / *italic*, `code`, and "# heading" lines → React elements. */
function InlineMarkdown({ text }) {
  const withHeadings = String(text || "").replace(/^#{1,6}\s+(.+)$/gm, "**$1**");
  const parts = withHeadings.split(/(\*\*[^*\n]+\*\*|`[^`\n]+`|(?<![\w*])_[^_\n]+_(?!\w)|(?<![\w*])\*[^*\n]+\*(?![\w*]))/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part)) return <code key={i} className="rounded bg-black/5 px-1 text-[12px] dark:bg-white/10">{part.slice(1, -1)}</code>;
    if (/^_[^_]+_$/.test(part) || /^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    return <span key={i}>{part}</span>;
  });
}

function PlainText({ text }) {
  const urlPattern = /(https?:\/\/[^\s]+|\/uploads\/[^\s]+)/g;
  const parts = String(text || "").split(urlPattern);

  return (
    <div className="space-y-2">
      {parts.map((part, index) => {
        if (!part) return null;
        const clean = part.replace(/[),.;]+$/, "");
        const suffix = part.slice(clean.length);
        if (!/^(https?:\/\/|\/uploads\/)/.test(clean)) {
          return <span key={index}><InlineMarkdown text={part} /></span>;
        }

        const resolved = clean.startsWith("/uploads/") ? `${baseUrl}${clean}` : clean;
        const isImage = clean.startsWith("/uploads/")
          || /\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(clean);
        const isMap = /maps\.google\.com\/maps\?q=/i.test(clean);

        return (
          <span key={index} className="block">
            {isImage && (
              <a href={resolved} target="_blank" rel="noreferrer" className="block">
                <img
                  src={resolved}
                  alt="Attendance capture"
                  loading="lazy"
                  className="max-h-52 w-full rounded-md border border-gray-200 object-cover dark:border-slate-600"
                />
              </a>
            )}
            {isMap && (
              <iframe
                title={`Attendance location ${index + 1}`}
                src={resolved}
                loading="lazy"
                className="h-48 w-full rounded-md border border-gray-200 dark:border-slate-600"
              />
            )}
            {!isImage && !isMap && (
              <a
                href={resolved}
                target="_blank"
                rel="noreferrer"
                className="break-all text-blue-600 underline dark:text-blue-300"
              >
                {clean}
              </a>
            )}
            {suffix}
          </span>
        );
      })}
    </div>
  );
}

function loadMessages() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    if (Array.isArray(saved) && saved.length) return saved;
  } catch {
    // Use the welcome message when stored data is invalid.
  }
  return [WELCOME];
}
